/**
 * Browser side of the application source (rules in ./applicationSource.ts).
 *
 * rememberJobSource() runs whenever a job page (/jobs/:jobId or /jobs/:jobId/apply) is shown
 * (src/pages/public/PublicJobGate.tsx wraps both for everyone). It keeps one channel per job in
 * sessionStorage, like the post-login redirect, so the tag survives sign-up, Google sign-in and the
 * multi-step form in this tab:
 *   - a ?src= tag (or a Google referrer) always wins: the latest tagged link is where they came from;
 *   - a landing with no tag counts as "direct" only when this tab has nothing for that job yet, so
 *     coming back to the job from the dashboard after a careers-page visit keeps "careers".
 * readJobSource() is what applyToJob sends; the server normalizes it again.
 */
import { channelFromLanding, normalizeSourceChannel, type SourceChannel } from './applicationSource';

export const JOB_SOURCE_STORAGE_KEY = 'arevinaJobSources';
const MAX_ENTRIES = 25;

type Stored = Record<string, { c: string; t: number }>;

function load(): Stored {
  try {
    const raw = sessionStorage.getItem(JOB_SOURCE_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? (parsed as Stored) : {};
  } catch {
    return {};
  }
}

function save(map: Stored): void {
  try {
    const entries = Object.entries(map).sort((a, b) => (b[1]?.t || 0) - (a[1]?.t || 0)).slice(0, MAX_ENTRIES);
    sessionStorage.setItem(JOB_SOURCE_STORAGE_KEY, JSON.stringify(Object.fromEntries(entries)));
  } catch {
    // Storage blocked (private mode etc.): the application is recorded as a direct link.
  }
}

/** Record where this visit to a job page came from. */
export function rememberJobSource(jobId: string, search?: string, referrer?: string | null): void {
  if (!jobId) return;
  const tagged = channelFromLanding(search ?? '', referrer ?? null);
  const map = load();
  if (tagged) {
    map[jobId] = { c: tagged, t: Date.now() };
    save(map);
  } else if (!map[jobId]) {
    map[jobId] = { c: 'direct', t: Date.now() };
    save(map);
  }
}

/** Remember a channel for a job before an in-app action that never opens its page (quick apply). */
export function rememberJobSourceIfUnset(jobId: string, channel: SourceChannel): void {
  if (!jobId) return;
  const map = load();
  if (map[jobId]) return;
  map[jobId] = { c: channel, t: Date.now() };
  save(map);
}

/** The channel to send with an application for this job ("direct" when nothing was recorded). */
export function readJobSource(jobId: string): SourceChannel {
  const entry = load()[jobId];
  return normalizeSourceChannel(entry?.c ?? null);
}

/**
 * document.referrer, but only when this tab LANDED on this job's page (the referrer belongs to the
 * first page load, so after in-app navigation it says nothing about how they reached this job).
 */
export function landingReferrerFor(jobId: string): string | null {
  try {
    const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
    const landing = nav?.name ? new URL(nav.name).pathname : null;
    if (!landing || !landing.startsWith(`/jobs/${jobId}`)) return null;
    return document.referrer || null;
  } catch {
    return null;
  }
}
